import { c as _c } from "react/compiler-runtime";
import { usePermissions } from '@openclassrooms/data-connect-web/users';
import { useCurrentUserContext } from '@openclassrooms/data-connect-web/users';
import { useGetProfileCompletion } from '@openclassrooms/data-connect-web/users';
import { useGet } from '@openclassrooms/data-connect-web/hooks';
import { ROUTES, getPathName } from '@openclassrooms/routing';
import useEmployerSpaceLink from './useEmployerSpaceLink';
const NOTIFICATION_COUNTERS_POLLING_INTERVAL_MS = 2 * 60 * 1000;
type HeaderConfiguration = {
  user?: User;
  right: Right;
  counters: {
    notificationCount: number;
    moderationNotificationCount: number;
    messageCount: number;
  };
  isConnected: boolean;
};
interface User {
  id?: number | null;
  displayableName?: string;
  profilePicture?: string;
  profileCompletionRate?: number | null;
  organizationName?: string;
  link: {
    profile?: string;
    logOut?: string;
    employerSpace?: string;
    parameter?: string;
  };
}
interface Right {
  canSeeMentorDashboard: boolean;
  canSeeAdminDashboard: boolean;
  canSeeTutorDashboard: boolean;
  canSeeOrganizationDashboard: boolean;
  canEditCourse: boolean;
  canDisplayForumAlert: boolean;
}
function useHeaderConfiguration() {
  const $ = _c(43);
  const {
    currentUser: user,
    isConnected
  } = useCurrentUserContext();
  const t0 = user?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      userId: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(user?.id);
  let t3;
  if ($[2] !== t2) {
    t3 = {
      enabled: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: userProfileCompletionRate
  } = useGetProfileCompletion(t1, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = Boolean(user?.id);
  let t6;
  if ($[5] !== t5) {
    t6 = {
      retry: 0,
      enabled: t5
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const {
    can
  } = usePermissions(t4, t6);
  const t7 = Boolean(user?.id);
  let t8;
  if ($[7] !== t7) {
    t8 = {
      enabled: t7,
      refetchInterval: NOTIFICATION_COUNTERS_POLLING_INTERVAL_MS,
      refetchOnWindowFocus: true
    };
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const {
    data: t9
  } = useGet("/me/notification/counters", t8);
  let t10;
  if ($[9] !== t9) {
    t10 = t9 === undefined ? {} : t9;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const {
    body: t11
  } = t10;
  let t12;
  if ($[11] !== t11) {
    t12 = t11 === undefined ? {} : t11;
    $[11] = t11;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const counters = t12;
  const employerSpaceLink = useEmployerSpaceLink(user);
  let t13;
  if ($[13] !== employerSpaceLink || $[14] !== user || $[15] !== userProfileCompletionRate?.completion) {
    t13 = user ? {
      id: user.id,
      displayName: user.displayName,
      organizationName: user.organization?.name,
      profilePicture: user.profilePicture,
      profileCompletionRate: userProfileCompletionRate?.completion,
      link: {
        profile: getPathName(ROUTES.MEMBERS.SHOW, {
          id: user.publicId
        }),
        employerSpace: employerSpaceLink,
        parameter: getPathName(ROUTES.MEMBERS.PARAMETERS, {
          id: user.publicId
        })
      }
    } : undefined;
    $[13] = employerSpaceLink;
    $[14] = user;
    $[15] = userProfileCompletionRate?.completion;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const t14 = counters?.notifications ?? 0;
  const t15 = counters?.alerts ?? 0;
  const t16 = counters?.unreadPrivateMessages ?? 0;
  let t17;
  if ($[17] !== t14 || $[18] !== t15 || $[19] !== t16) {
    t17 = {
      notificationCount: t14,
      moderationNotificationCount: t15,
      messageCount: t16
    };
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== can) {
    t18 = can("CAN_SEE_ADMIN_DASHBOARD");
    $[21] = can;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== can) {
    t19 = can("CAN_SEE_MENTOR_DASHBOARD");
    $[23] = can;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== can) {
    t20 = can("CAN_SEE_TUTOR_DASHBOARD");
    $[25] = can;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== can) {
    t21 = can("CAN_SEE_ORGANIZATION_DASHBOARD");
    $[27] = can;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== can) {
    t22 = can("CAN_EDIT_COURSE");
    $[29] = can;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  const t23 = (counters?.alerts ?? 0) > 0;
  let t24;
  if ($[31] !== t18 || $[32] !== t19 || $[33] !== t20 || $[34] !== t21 || $[35] !== t22 || $[36] !== t23) {
    t24 = {
      canSeeAdminDashboard: t18,
      canSeeMentorDashboard: t19,
      canSeeTutorDashboard: t20,
      canSeeOrganizationDashboard: t21,
      canEditCourse: t22,
      canDisplayForumAlert: t23
    };
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
    $[34] = t21;
    $[35] = t22;
    $[36] = t23;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== isConnected || $[39] !== t13 || $[40] !== t17 || $[41] !== t24) {
    t25 = {
      user: t13,
      counters: t17,
      right: t24,
      isConnected
    };
    $[38] = isConnected;
    $[39] = t13;
    $[40] = t17;
    $[41] = t24;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  return t25;
}
export default useHeaderConfiguration;